import { useEffect } from "react";
import { createPortal } from "react-dom";
import { LoadingPulse } from "./LoadingPulse";

function isReloadKey(event) {
  if (event.key === "F5") return true;
  if ((event.ctrlKey || event.metaKey) && (event.key === "r" || event.key === "R")) return true;
  return false;
}

/**
 * Full-viewport lock: blocks clicks and refresh while work is in flight.
 * Scroll lock is handled by ModalBodyScrollLock (watches this overlay class).
 *
 * Optional progress:
 *   progressPct — 0..100
 *   steps — [{ label, status: "done"|"active"|"pending" }]
 */
export function BlockingActionOverlay({
  open,
  title,
  subtitle,
  progressPct = null,
  steps = null,
}) {
  useEffect(() => {
    if (!open || typeof window === "undefined") return undefined;

    const root = document.getElementById("root");
    if (root) root.inert = true;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const onKeyDown = (event) => {
      if (!isReloadKey(event)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("keydown", onKeyDown, true);

    return () => {
      // Always clear — do not restore a prior stuck inert=true.
      if (root) root.inert = false;
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  const pct =
    progressPct != null && Number.isFinite(Number(progressPct))
      ? Math.max(0, Math.min(100, Math.round(Number(progressPct))))
      : null;
  const stepList = Array.isArray(steps) ? steps.filter((s) => s && s.label) : [];

  return createPortal(
    <div
      className="blocking-action-overlay"
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-live="assertive"
      aria-label={title}
    >
      <div
        className="blocking-action-card"
        style={stepList.length || pct != null ? { maxWidth: 420, width: "100%" } : undefined}
      >
        <LoadingPulse size={28} />
        <div className="blocking-action-title">{title}</div>
        {subtitle ? <div className="blocking-action-subtitle">{subtitle}</div> : null}
        {pct != null ? (
          <div className="blocking-action-progress" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} role="progressbar">
            <div className="blocking-action-progress-track">
              <div
                className="blocking-action-progress-fill"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="blocking-action-progress-label">{pct}%</div>
          </div>
        ) : null}
        {stepList.length ? (
          <ol className="blocking-action-steps">
            {stepList.map((step, i) => (
              <li
                key={`${step.label}-${i}`}
                className={`blocking-action-step blocking-action-step--${step.status || "pending"}`}
              >
                <span className="blocking-action-step-mark" aria-hidden>
                  {step.status === "done" ? "✓" : step.status === "active" ? "●" : "○"}
                </span>
                <span className="blocking-action-step-label">{step.label}</span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
